
import apiEndPoints from "../../../routeConfig";
import { ApiService } from "../../../../service/api.service";
import axiosInstance from "../../../../service/axios.instance";
import { attempt } from "../../../../service/apiResult";

const apiService = new ApiService();

export const initiativeCreateOrUpdate = async (data, editMode, id) => attempt(() => {
  if (editMode && id) {
    return axiosInstance
      .patch(`${process.env.REACT_APP_API_URL}${apiEndPoints.initiativeGetById}/${id}`, data)
      .then((response) => response.data);
  }

  // Determine if it's admin or creator initiative based on data structure
  const isAdmin = data.sector && data.countryIds;
  const url = isAdmin
    ? apiEndPoints.initiativeCreateAdmin
    : apiEndPoints.initiativeCreateCreator;

  return apiService.post(url, data);
})

export const getAllInitiative = async (page = 0, size = 10) => {
  let result;
  await apiService
    .get(`${apiEndPoints.getAllInitiative}?page=${page}&size=${size}`).then((response) => {
      result = response;
    })
    .catch((error) => {
      result = error;
    });
  return result;
};

export const initiativeGetById = async (id) => {
  let result;
  await apiService.get(`${apiEndPoints.initiativeGetById}/${id}`)
    .then((response) => {
      result = response.data;
    })
    .catch(error => {
      result = error;
    });
  return result;
};

export const deleteInitiative = async (id) =>
  attempt(() => axiosInstance.delete(`${process.env.REACT_APP_API_URL}${apiEndPoints.deleteInitiative}/${id}`));

export const initiativePatchApi = async (id, statusUpdate) =>
  attempt(() => apiService.patch(`${apiEndPoints.initiativePatchApi}/${id}/status/${statusUpdate}`, ''));

export const countryMetaData = async () => {
  return await apiService.get(`${apiEndPoints.countryMetaData}`)
    .then((response) => {
      return response;
    })
    .catch(error => {
      return error;
    });
};

export const uploadInitiativeImage = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const apiUrl = process.env.REACT_APP_API_URL;
    const response = await axiosInstance.post(
      apiUrl + 'api/v1/ekutir/initiative/upload-image',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};


// Decision D5: super admin attaches Initiative Based Ecosystem Integrators to an Initiative.
const integratorsUrl = (id) => `${apiEndPoints.initiativeGetById}/${id}/integrators`;

export const getInitiativeIntegrators = async (id) =>
  attempt(() => apiService.get(integratorsUrl(id)).then((response) => response.data));

export const getInitiativeIntegratorCandidates = async (id) =>
  attempt(() => apiService.get(`${integratorsUrl(id)}/candidates`).then((response) => response.data));

export const attachInitiativeIntegrator = async (id, integratorUserId) =>
  attempt(() => apiService.post(integratorsUrl(id), { integratorUserId }));

// Also removes the Initiative from the integrator's sign-up choice; the reason is kept in the history.
export const detachInitiativeIntegrator = async (id, integratorUserId, reason) =>
  attempt(() =>
    apiService.delete(
      `${integratorsUrl(id)}/${integratorUserId}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`
    )
  );

// Initiative approval chain (P2-2): area-based integrators taking part in an Initiative, and eKutir's
// override to join or withdraw on an area's behalf.
const areaParticipationsUrl = (id) => `${apiEndPoints.initiativeGetById}/${id}/area-participations`;

export const getInitiativeAreaParticipations = async (id) =>
  attempt(() => apiService.get(areaParticipationsUrl(id)).then((response) => response.data));

export const approveInitiativeAreaParticipation = async (id, areaEcosystemId, note) =>
  attempt(() => apiService.post(`${areaParticipationsUrl(id)}/${areaEcosystemId}/approve`, { note }));

// The reason is required and kept in the history.
export const withdrawInitiativeAreaParticipation = async (id, areaEcosystemId, reason) =>
  attempt(() => apiService.post(`${areaParticipationsUrl(id)}/${areaEcosystemId}/withdraw`, { reason }));

// Sub-initiatives: one level of initiatives that approved players (service providers and initiative-based
// integrators) create under an Initiative. eKutir can create one on a player's behalf and manage its status.
const subInitiativesUrl = (id) => `${apiEndPoints.initiativeGetById}/${id}/sub-initiatives`;
const subInitiativeUrl = (subInitiativeId) => `api/v1/ekutir/sub-initiatives/${subInitiativeId}`;

export const getSubInitiatives = async (id) =>
  attempt(() => apiService.get(subInitiativesUrl(id)).then((response) => response.data));

export const getSubInitiativeCreators = async (id) =>
  attempt(() => apiService.get(`${subInitiativesUrl(id)}/creators`).then((response) => response.data));

// body: { creatorUserId, name, description, countryIds, startDate, endDate }
export const createSubInitiativeOnBehalf = async (id, body) =>
  attempt(() => apiService.post(subInitiativesUrl(id), body));

// body: { name?, description?, countryIds?, startDate?, endDate? }
export const updateSubInitiative = async (subInitiativeId, body) =>
  attempt(() => apiService.patch(subInitiativeUrl(subInitiativeId), body));

// status: ACTIVE | PAUSED | CLOSED; the reason is required for PAUSED and CLOSED.
export const changeSubInitiativeStatus = async (subInitiativeId, status, reason) =>
  attempt(() => apiService.post(`${subInitiativeUrl(subInitiativeId)}/status`, { status, reason }));
